<template>
  <!-- Kliendid -> Otsi duplikaate in the new look (Seaded -> Kliendid uues
       kujunduses), agreed on a mockup first: the groups by kind with counts,
       a merge card per group, "Pole duplikaat" and Peidetud, and a manual
       pick for clients the scan cannot pair. -->
  <div class="dp">
    <div class="dp-head">
      <div><div class="dp-cap">Kliendid</div><h1 class="dp-title">Otsi duplikaate</h1></div>
    </div>
    <p class="dp-lede">Iga kaart on üks grupp: kliendid, kellel on sama telefon, e-post või nimi. Kollasega on see, mis kattub. Vali, kes jääb alles; teiste objektid, töökäsud ja aktid liiguvad talle ning tühjad väljad (e-post, telefon, aadress) täidetakse.</p>

    <div class="dp-chips">
      <button
        v-for="chip in chipList"
        :key="chip.key"
        type="button"
        class="dp-chip"
        :class="{ on: kind === chip.key }"
        @click="setKind(chip.key)"
      >{{ chip.text }}<small>{{ chip.count == null ? '…' : number(chip.count) }}</small></button>
    </div>

    <div class="dp-manual">
      <b>Ühenda käsitsi</b>
      <div class="dp-manual__search">
        <input v-model="manualQuery" type="search" placeholder="Otsi klienti nime või telefoni järgi ja lisa kaardile" aria-label="Otsi klienti">
        <div v-if="manualHits.length" class="dp-hits">
          <button v-for="hit in manualHits" :key="hit.id" type="button" class="dp-hit" @click="addManual(hit.id)">
            <b>{{ hit.fullName }}</b> <span class="dp-sub">{{ [hit.phone, hit.email].filter(Boolean).join(' · ') }} · id {{ hit.id }}</span>
          </button>
        </div>
      </div>
      <span class="dp-sub">Kui duplikaati ei leitud, sest andmed erinevad.</span>
    </div>
    <client-duplicate-card
      v-if="manual.clients.length"
      :group="manual"
      mode="manual"
      :busy="busy === 'manual'"
      :done="doneText.manual || ''"
      :done-id="doneId.manual || null"
      @remove="removeManual"
      @merge="merge('manual', $event)"
    />

    <v-progress-linear :active="loading" indeterminate color="green" height="3" class="mb-2" />
    <client-duplicate-card
      v-for="group in groups"
      :key="group.key"
      :group="group"
      :mode="kind === 'hidden' ? 'hidden' : 'found'"
      :busy="busy === group.key"
      :done="doneText[group.key] || ''"
      :done-id="doneId[group.key] || null"
      @merge="merge(group.key, $event)"
      @dismiss="dismiss(group.key, $event)"
      @restore="restore(group.key, $event)"
    />
    <div v-if="!loading && !groups.length" class="dp-empty">{{ kind === 'hidden' ? 'Peidetud gruppe pole.' : 'Selles liigis duplikaate pole.' }}</div>
    <div v-if="groups.length" class="dp-more">
      <span>Näidatud {{ groups.length }} / {{ number(total) }}</span>
      <v-btn v-if="kind !== 'hidden' && groups.length < total" variant="outlined" :loading="loading" @click="load(true)">Näita veel</v-btn>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { mapActions } from 'vuex'
import ClientDuplicateCard from '@/components/ClientDuplicateCard.vue'

const PAGE = 20

export default {
  components: { ClientDuplicateCard },
  data () {
    return {
      kind: 'all',
      groups: [],
      total: 0,
      counts: {},
      hiddenCount: null,
      loading: false,
      busy: null,
      doneText: {},
      doneId: {},
      manualQuery: '',
      manualHits: [],
      manualTimer: null,
      // the picks' rows; a newer pick supersedes an answer still on its way
      manual: { key: 'manual', reasons: [], size: 0, clients: [] },
      manualAsk: 0,
      requestNo: 0
    }
  },
  computed: {
    chipList () {
      const c = this.counts
      return [
        { key: 'all', text: 'Kõik', count: c.all },
        { key: 'easy', text: 'Teine on tühi', count: c.easy },
        { key: 'strong', text: 'Sama nimi ja telefon', count: c.strong },
        { key: 'phone', text: 'Sama telefon', count: c.phone },
        { key: 'email', text: 'Sama e-post', count: c.email },
        { key: 'nameOnly', text: 'Ainult nimi — kontrolli', count: c.nameOnly },
        { key: 'big', text: 'Ühine kontakt 6+', count: c.big },
        { key: 'hidden', text: 'Peidetud', count: this.hiddenCount }
      ]
    }
  },
  watch: {
    manualQuery (value) {
      clearTimeout(this.manualTimer)
      if (!value || value.trim().length < 2) {
        this.manualHits = []
        return
      }
      this.manualTimer = setTimeout(async () => {
        try {
          const { clients } = await api.getClients({ q: value.trim(), limit: 8 })
          const picked = new Set(this.manual.clients.map(c => c.id))
          this.manualHits = clients.filter(c => !picked.has(c.id))
        } catch (err) {
          this.manualHits = []
        }
      }, 300)
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    number (n) {
      return n == null ? '' : Number(n).toLocaleString('et-EE')
    },
    setKind (kind) {
      if (kind === this.kind) return
      this.kind = kind
      this.load()
    },
    keyOf (group) {
      return group.ids.join('-')
    },
    async load (more = false) {
      const no = ++this.requestNo
      this.loading = true
      try {
        if (this.kind === 'hidden') {
          const { groups, count } = await api.getClientNotDuplicates()
          if (no !== this.requestNo) return
          this.groups = groups.map(g => ({ ...g, key: this.keyOf(g), reasons: [], size: g.ids.length }))
          this.total = count
          this.hiddenCount = count
        } else {
          const result = await api.getClientDuplicateGroups({ kind: this.kind, offset: more ? this.groups.length : 0, limit: PAGE })
          if (no !== this.requestNo) return
          const page = result.groups.map(g => ({ ...g, key: this.keyOf(g) }))
          this.groups = more ? this.groups.concat(page) : page
          this.total = result.total
          this.counts = result.counts
        }
      } catch (err) {
        if (no === this.requestNo) this.showNotification({ text: err.message, color: 'red' })
      } finally {
        if (no === this.requestNo) this.loading = false
      }
    },
    async loadHiddenCount () {
      try {
        this.hiddenCount = (await api.getClientNotDuplicates()).count
      } catch (err) {
        this.hiddenCount = null
      }
    },
    // N -> 1, one pair at a time, as POST /client/merge merges.
    async merge (key, { keepId, deleteIds, leftOut = [] }) {
      // One merge at a time: `busy` holds one group, and a second started
      // meanwhile had its button freed when the first finished.
      if (this.busy) return
      this.busy = key
      const group = key === 'manual' ? this.manual : this.groups.find(g => g.key === key)
      const keep = group.clients.find(c => c.id === keepId)
      let merged = 0
      try {
        for (const deleteId of deleteIds) {
          await api.mergeClients({ keepId, deleteId })
          merged++
        }
        // The ones left out are different clients: remembered against the one
        // that stays, or the scan would offer them together again. (A manual
        // pick was never offered, so nothing to remember.)
        if (key !== 'manual') {
          for (const id of leftOut) await api.markClientsNotDuplicate([keepId, id])
        }
        this.doneText = { ...this.doneText, [key]: 'Ühendatud: ' + merged + ' ' + (merged === 1 ? 'klient' : 'klienti') + ' → ' + keep.fullName + ' (id ' + keepId + ')' +
          (leftOut.length ? '; ' + leftOut.length + ' jäi kõrvale' : '') }
        this.doneId = { ...this.doneId, [key]: keepId }
        this.showNotification({ text: this.doneText[key], color: 'success' })
      } catch (err) {
        this.showNotification({ text: (merged ? merged + ' ühendati, siis: ' : '') + (err.message || 'Ühendamine ebaõnnestus'), color: 'red' })
      } finally {
        this.busy = null
      }
    },
    async dismiss (key, ids) {
      this.busy = key
      try {
        await api.markClientsNotDuplicate(ids)
        this.groups = this.groups.filter(g => g.key !== key)
        this.total = Math.max(0, this.total - 1)
        this.hiddenCount = (this.hiddenCount || 0) + 1
        this.showNotification({ text: 'Peidetud. Peidetud kiibilt saab taastada.', color: 'success' })
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.busy = null
      }
    },
    async restore (key, ids) {
      this.busy = key
      try {
        await api.restoreClientDuplicates(ids)
        this.groups = this.groups.filter(g => g.key !== key)
        this.total = Math.max(0, this.total - 1)
        this.hiddenCount = Math.max(0, (this.hiddenCount || 1) - 1)
        this.showNotification({ text: 'Taastatud.', color: 'success' })
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.busy = null
      }
    },
    async refreshManual (ids) {
      const ask = ++this.manualAsk
      const { clients } = ids.length ? await api.getClientMergePreview(ids) : { clients: [] }
      if (ask !== this.manualAsk) return
      this.manual = { ...this.manual, clients, size: clients.length }
    },
    addManual (id) {
      this.manualQuery = ''
      this.manualHits = []
      this.doneText = { ...this.doneText, manual: '' }
      this.refreshManual([...this.manual.clients.map(c => c.id), id]).catch(err => this.showNotification({ text: err.message, color: 'red' }))
    },
    removeManual (id) {
      this.refreshManual(this.manual.clients.map(c => c.id).filter(x => x !== id)).catch(() => {})
    }
  },
  mounted () {
    this.load()
    this.loadHiddenCount()
  },
  beforeUnmount () {
    clearTimeout(this.manualTimer)
  }
}
</script>

<style scoped>
.dp { font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #16322A; padding-bottom: 40px; }
.dp button, .dp input { font: inherit; color: inherit; }
.dp-head { display: flex; flex-wrap: wrap; align-items: end; justify-content: space-between; gap: 12px; margin-bottom: 6px; }
.dp-cap { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: #5B6B66; font-weight: 600; }
.dp-title { font-size: 30px; font-weight: 600; margin: 2px 0 0; letter-spacing: -.01em; line-height: 1.2; }
.dp-lede { color: #5B6B66; margin: 0 0 14px; max-width: 74ch; }
.dp-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
.dp-chip { border: 1px solid #C9D2CE; background: #fff; border-radius: 999px; padding: 5px 12px; cursor: pointer; font-size: 13px; font-variant-numeric: tabular-nums; }
.dp-chip small { color: #5B6B66; margin-left: 4px; }
.dp-chip.on { background: #16322A; border-color: #16322A; color: #fff; }
.dp-chip.on small { color: inherit; opacity: .7; }
.dp-manual { background: #fff; border: 1px dashed #C9D2CE; border-radius: 10px; padding: 12px 14px; margin-bottom: 16px; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.dp-manual__search { flex: 1 1 280px; min-width: 0; position: relative; }
.dp-manual__search input { width: 100%; border: 1px solid #C9D2CE; border-radius: 8px; padding: 8px 12px; background: #fff; }
.dp-hits { position: absolute; z-index: 3; left: 0; right: 0; top: calc(100% + 4px); background: #fff; border: 1px solid #C9D2CE; border-radius: 8px; box-shadow: 0 6px 20px rgba(0, 0, 0, .12); overflow: hidden; }
.dp-hit { display: block; width: 100%; text-align: left; border: 0; background: none; padding: 8px 12px; cursor: pointer; }
.dp-hit:hover { background: #F4F6F5; }
.dp-sub { color: #5B6B66; font-size: 12px; }
.dp-empty { background: #fff; border: 1px dashed #C9D2CE; border-radius: 10px; padding: 30px; text-align: center; color: #5B6B66; }
.dp-more { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 10px; color: #5B6B66; font-variant-numeric: tabular-nums; }
</style>
